import { useQuery } from '@tanstack/react-query';
import { API_BASE } from '../utils';
import { apiFetch } from './fetch-utils';
import type { PlayerSearchResponse, PlayerSeasonStatsResponse, PlayerRecentMatchResponse, PlayerRecentMatchesResponse, PlayerHistoryResponse, PlayerSeasonSummaryResponse, PlayerSalaryProgressionResponse, PlayerMatchStatsResponse, PlayerYearStatsResponse, PlayerPercentileResponse, PlayerStatScatterResponse, PlayerCompetitionsResponse, FaveOpponentsResponse } from '../types/players';

// GET /players/{player_id} — returns a single-element array (same shape as
// /players/search), not a bare object.
interface PlayerParams {
  player_id: number;
}

const fetchPlayer = ({ player_id }: PlayerParams) =>
  apiFetch<PlayerSearchResponse>(`${API_BASE}/players/${player_id}`)

export const usePlayer = (params: PlayerParams) =>
  useQuery({
    queryKey: ['player', params.player_id],
    queryFn: () => fetchPlayer(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/recent-match
const fetchPlayerRecentMatch = ({ player_id }: PlayerParams) =>
  apiFetch<PlayerRecentMatchResponse>(`${API_BASE}/players/${player_id}/recent-match`)

export const usePlayerRecentMatch = (params: PlayerParams) =>
  useQuery({
    queryKey: ['playerRecentMatch', params.player_id],
    queryFn: () => fetchPlayerRecentMatch(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/recent-matches
interface PlayerRecentMatchesParams {
  player_id: number;
  limit?: number;
  offset?: number;
}

const fetchPlayerRecentMatches = ({ player_id, limit = 5, offset = 0 }: PlayerRecentMatchesParams) =>
  apiFetch<PlayerRecentMatchesResponse>(`${API_BASE}/players/${player_id}/recent-matches?limit=${limit}&offset=${offset}`)

export const usePlayerRecentMatches = (params: PlayerRecentMatchesParams) =>
  useQuery({
    queryKey: ['playerRecentMatches', params.player_id, params.limit, params.offset ?? 0],
    queryFn: () => fetchPlayerRecentMatches(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/history
const fetchPlayerHistory = ({ player_id }: PlayerParams) =>
  apiFetch<PlayerHistoryResponse>(`${API_BASE}/players/${player_id}/history`)

export const usePlayerHistory = (params: PlayerParams) =>
  useQuery({
    queryKey: ['playerHistory', params.player_id],
    queryFn: () => fetchPlayerHistory(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/season-summary
interface PlayerSeasonSummaryParams {
  player_id: number;
  season_year?: number;
  stat?: string;
}

const fetchPlayerSeasonSummary = ({ player_id, season_year = 2026, stat = 'ga' }: PlayerSeasonSummaryParams) =>
  apiFetch<PlayerSeasonSummaryResponse>(`${API_BASE}/players/${player_id}/season-summary?season_year=${season_year}&stat=${stat}`)

export const usePlayerSeasonSummary = (params: PlayerSeasonSummaryParams) =>
  useQuery({
    queryKey: ['playerSeasonSummary', params.player_id, params.season_year, params.stat],
    queryFn: () => fetchPlayerSeasonSummary(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/match-stats
interface PlayerMatchStatsParams {
  player_id: number;
  season_year?: number;
  stat?: string;
  comp_ids?: number[] | null;
}

const fetchPlayerMatchStats = ({ player_id, season_year = 2026, stat = 'ga', comp_ids }: PlayerMatchStatsParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<PlayerMatchStatsResponse>(`${API_BASE}/players/${player_id}/match-stats?${params}`)
}

export const usePlayerMatchStats = (params: PlayerMatchStatsParams) =>
  useQuery({
    queryKey: ['playerMatchStats', params.player_id, params.season_year, params.stat, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchPlayerMatchStats(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/percentile
interface PlayerPercentileParams {
  player_id: number;
  season_year?: number;
  stat?: string;
  comp_ids?: number[] | null;
}

const fetchPlayerPercentile = ({ player_id, season_year = 2026, stat = 'ga', comp_ids }: PlayerPercentileParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<PlayerPercentileResponse>(`${API_BASE}/players/${player_id}/percentile?${params}`)
}

export const usePlayerPercentile = (params: PlayerPercentileParams) =>
  useQuery({
    queryKey: ['playerPercentile', params.player_id, params.season_year, params.stat, params.comp_ids?.join(',') ?? null],
    queryFn: () => fetchPlayerPercentile(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/stat-scatter
interface PlayerStatScatterParams {
  player_id: number;
  season_year?: number;
  stat?: string;
  comp_ids?: number[] | null;
  group?: 'all' | 'age' | 'position' | 'minutes';
}

const fetchPlayerStatScatter = ({ player_id, season_year = 2026, stat = 'ga', comp_ids, group = 'all' }: PlayerStatScatterParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat, group });
  if (comp_ids && comp_ids.length > 0) params.set('comp_ids', comp_ids.join(','));
  return apiFetch<PlayerStatScatterResponse>(`${API_BASE}/players/${player_id}/stat-scatter?${params}`)
}

export const usePlayerStatScatter = (params: PlayerStatScatterParams) =>
  useQuery({
    queryKey: ['playerStatScatter', params.player_id, params.season_year, params.stat, params.comp_ids?.join(',') ?? null, params.group ?? 'all'],
    queryFn: () => fetchPlayerStatScatter(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/competitions
interface PlayerCompetitionsParams {
  player_id: number;
  season_year?: number;
}

const fetchPlayerCompetitions = ({ player_id, season_year = 2026 }: PlayerCompetitionsParams) =>
  apiFetch<PlayerCompetitionsResponse>(`${API_BASE}/players/${player_id}/competitions?season_year=${season_year}`)

export const usePlayerCompetitions = (params: PlayerCompetitionsParams) =>
  useQuery({
    queryKey: ['playerCompetitions', params.player_id, params.season_year],
    queryFn: () => fetchPlayerCompetitions(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/year-stats
interface PlayerYearStatsParams {
  player_id: number;
  stat?: string;
}

const fetchPlayerYearStats = ({ player_id, stat = 'ga' }: PlayerYearStatsParams) =>
  apiFetch<PlayerYearStatsResponse>(`${API_BASE}/players/${player_id}/year-stats?stat=${stat}`)

export const usePlayerYearStats = (params: PlayerYearStatsParams) =>
  useQuery({
    queryKey: ['playerYearStats', params.player_id, params.stat],
    queryFn: () => fetchPlayerYearStats(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /players/{player_id}/salary-progression
const fetchPlayerSalaryProgression = ({ player_id }: PlayerParams) =>
  apiFetch<PlayerSalaryProgressionResponse>(`${API_BASE}/players/${player_id}/salary-progression`)

export const usePlayerSalaryProgression = (params: PlayerParams) =>
  useQuery({
    queryKey: ['playerSalaryProgression', params.player_id],
    queryFn: () => fetchPlayerSalaryProgression(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })


// GET /players/search
interface PlayerSearchParams {
  q: string;
  limit?: number;
}
const fetchPlayerSearch = ({ q, limit = 15 }: PlayerSearchParams) => {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch<PlayerSearchResponse>(`${API_BASE}/players/search?${params}`)
}

export const usePlayerSearch = (params: PlayerSearchParams) =>
  useQuery({
    queryKey: ['playerSearch', params.q, params.limit],
    queryFn: () => fetchPlayerSearch(params),
    enabled: params.q.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })



// GET /players/season-stats
interface PlayerSeasonStatsParams {
  player_ids: number[];
  season: number;
}

const fetchPlayerSeasonStats = ({ player_ids, season }: PlayerSeasonStatsParams) => {
  const params = new URLSearchParams({ season: String(season) });
  player_ids.forEach((id) => params.append('player_ids', String(id)));
  return apiFetch<PlayerSeasonStatsResponse>(`${API_BASE}/players/season-stats?${params}`);
}

export const usePlayerSeasonStats = (params: PlayerSeasonStatsParams) =>
  useQuery({
    queryKey: ['playerSeasonStats', params.player_ids, params.season],
    queryFn: () => fetchPlayerSeasonStats(params),
    enabled: params.player_ids.length > 0 && params.season > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })



// GET /players/{player_id}/fave-opponents
interface FaveOpponentsParams {
  player_id: number;
}

const fetchFaveOpponents = ({ player_id }: FaveOpponentsParams) =>
  apiFetch<FaveOpponentsResponse>(`${API_BASE}/players/${player_id}/fave-opponents`)

export const useFaveOpponents = (params: FaveOpponentsParams) =>
  useQuery({
    queryKey: ['faveOpponents', params.player_id],
    queryFn: () => fetchFaveOpponents(params),
    enabled: params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })
